import * as React from 'react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { useEffect, useRef, useState } from 'react';
import Slide from '@mui/material/Slide';
import { TransitionProps } from '@mui/material/transitions';
import Input from '@mui/material/Input';
import { Box, Stack, Tooltip, Typography } from '@mui/material';

interface DialogBoxFolderProps {
    openDialogBox: boolean;
    handleFunction?: (file: File | null) => void;
    handleCancel?: () => void;
}

const Transition = React.forwardRef(function Transition(
    props: TransitionProps & {
        children: React.ReactElement<any, any>;
    },
    ref: React.Ref<unknown>,
) {
    return <Slide direction="up" ref={ref} {...props} />;
});

const DialogBoxUploadFiles: React.FC<DialogBoxFolderProps> = (props) => {
    const [open, setOpen] = useState(false);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const fileInputRef = useRef<HTMLInputElement | null>(null);

    useEffect(() => {
        setOpen(props.openDialogBox);
    }, [props.openDialogBox]);

    const handleClose = () => {
        if (props.handleCancel) {
            props.handleCancel();
        }
        setSelectedFile(null);
        setErrorMessage(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const handleOk = () => {
        if (props.handleFunction) {
            props.handleFunction(selectedFile); // Pass selectedFile directly
        }
        handleClose();

    };

    const handleFormSubmit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        handleOk();
    };

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.files && event.target.files[0]) {
            setSelectedFile(event.target.files[0]);
        }
    };
    const handleChooseFile = () => {
        fileInputRef.current?.click();
    };

    const getFormattedFileName = (fileName: string, maxLength: number) => {
        if (fileName.length <= maxLength) {
            return fileName;
        }
        const start = fileName.slice(0, Math.floor(maxLength / 2));
        const end = fileName.slice(-Math.floor(maxLength / 2));
        return `${start}...${end}`;
    };

    const maxLength = 30; // Maximum characters to show before and after the ellipsis
    const displayedFileName = selectedFile ? getFormattedFileName(selectedFile.name, maxLength) : "No Assessment chosen";
    return (
        <Dialog
            open={open}
            onClose={handleClose}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
            TransitionComponent={Transition}
            keepMounted
            PaperProps={{
                component: 'form',
                onSubmit: handleFormSubmit,
            }}
            fullWidth
            maxWidth="xs"
        >
            <DialogTitle id="alert-dialog-title">
                Upload Assessment
            </DialogTitle>
            <DialogContent>
                <Stack direction="row" spacing={2} alignItems="center">
                    <Button variant="outlined" onClick={handleChooseFile}>
                        {selectedFile ? "Change Assessment" : "Choose Assessment"}
                    </Button>
                    <Input
                        type="file"
                        inputProps={{ accept: '*/*' }}
                        inputRef={fileInputRef}
                        onChange={handleFileChange}
                        style={{ display: 'none' }}
                    />
                    <Tooltip title={selectedFile ? selectedFile.name : "No assessment chosen"}>
                        <Typography
                            variant="body1"
                            noWrap
                            sx={{
                                maxWidth: '300px', // Increased width
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                            }}
                        >
                            {displayedFileName}
                        </Typography>
                    </Tooltip>
                </Stack>

                {errorMessage && (
                    <Typography color="error" variant="body2" sx={{ mt: 1 }}>
                        {errorMessage}
                    </Typography>
                )}
            </DialogContent>
            <DialogActions>
                <Button
                    size='small'
                    variant="contained"
                    onClick={handleClose}
                    sx={{
                        backgroundColor: "rgba(0, 102, 255, 0.1)",
                        color: "#0066FF",
                        '&:hover': {
                            bgcolor: "#0066ff2e",
                        },
                    }}
                >
                    Cancel
                </Button>
                <Button
                    size='small'
                    variant='contained'
                    type="submit"
                    sx={{
                        backgroundColor: "#0066FF",
                        '&:hover': {
                            bgcolor: "#0556d0",
                        },
                    }}
                >
                    Upload
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default DialogBoxUploadFiles;
